/**
 * Graph view, the pure half: GraphResponse + viewer settings → what the
 * canvas draws. Adapted from Folio for note↔note graphs only (no tags/files/
 * unresolved/tree). Search is a simple case-insensitive substring over title
 * and path (words AND-ed; `-word` excludes).
 */
import type {
  GraphEdge,
  GraphNode,
  GraphNodeKind,
  GraphResponse,
} from '@bangle.io/service-core';

export type ColorBy = 'none' | 'folder';

export interface GraphGroup {
  query: string;
  color: string;
}

export interface GraphSettings {
  search: string;
  showOrphans: boolean;
  colorBy: ColorBy;
  groups: GraphGroup[];
  arrows: boolean;
  /** Zoom level from which every label is drawn (hovered and the centre always are). */
  labelZoom: number;
  sizeByDegree: boolean;
  nodeSize: number;
  linkThickness: number;
  centerForce: number;
  repelForce: number;
  linkForce: number;
  linkDistance: number;
  /** The local graph (page panel and centered full-page mode). */
  depth: number;
  direction: 'both' | 'in' | 'out';
}

export const DEFAULT_GRAPH_SETTINGS: GraphSettings = {
  search: '',
  showOrphans: true,
  colorBy: 'folder',
  groups: [],
  arrows: false,
  labelZoom: 1.1,
  sizeByDegree: true,
  nodeSize: 1,
  linkThickness: 1,
  centerForce: 0.3,
  repelForce: 8,
  linkForce: 0.6,
  linkDistance: 50,
  depth: 1,
  direction: 'both',
};

const clamp = (
  v: unknown,
  min: number,
  max: number,
  fallback: number,
): number =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.min(max, Math.max(min, v))
    : fallback;
const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;
const COLOR_RE = /^#[0-9a-f]{6}$/i;

/** Settings read back from localStorage: anything missing or malformed takes its default. */
export function sanitizeSettings(raw: unknown): GraphSettings {
  const d = DEFAULT_GRAPH_SETTINGS;
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    unknown
  >;
  const groups = Array.isArray(r.groups)
    ? r.groups
        .filter(
          (g): g is GraphGroup =>
            !!g &&
            typeof g === 'object' &&
            typeof (g as GraphGroup).query === 'string' &&
            COLOR_RE.test(String((g as GraphGroup).color)),
        )
        .slice(0, 12)
        .map((g) => ({ query: g.query.slice(0, 300), color: g.color }))
    : [];
  return {
    search: typeof r.search === 'string' ? r.search.slice(0, 300) : d.search,
    showOrphans: bool(r.showOrphans, d.showOrphans),
    colorBy: r.colorBy === 'none' || r.colorBy === 'folder' ? r.colorBy : d.colorBy,
    groups,
    arrows: bool(r.arrows, d.arrows),
    labelZoom: clamp(r.labelZoom, 0.2, 4, d.labelZoom),
    sizeByDegree: bool(r.sizeByDegree, d.sizeByDegree),
    nodeSize: clamp(r.nodeSize, 0.4, 3, d.nodeSize),
    linkThickness: clamp(r.linkThickness, 0.2, 4, d.linkThickness),
    centerForce: clamp(r.centerForce, 0, 1, d.centerForce),
    repelForce: clamp(r.repelForce, 0, 20, d.repelForce),
    linkForce: clamp(r.linkForce, 0, 1, d.linkForce),
    linkDistance: clamp(r.linkDistance, 10, 300, d.linkDistance),
    depth: Math.round(clamp(r.depth, 1, 3, d.depth)),
    direction:
      r.direction === 'in' || r.direction === 'out' || r.direction === 'both'
        ? r.direction
        : d.direction,
  };
}

export const isPageNode = (n: Pick<GraphNode, 'kind'>): boolean =>
  n.kind === 'doc';

/** Simple search: space-separated words (AND); prefix `-` excludes. */
export function compileQuery(
  q: string,
): ((n: GraphNode) => boolean) | null {
  const tokens = q
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) {
    return null;
  }
  const words: string[] = [];
  const excluded: string[] = [];
  for (const token of tokens) {
    if (token.startsWith('-') && token.length > 1) {
      excluded.push(token.slice(1).toLowerCase());
    } else {
      words.push(token.toLowerCase());
    }
  }
  if (words.length === 0 && excluded.length === 0) {
    return null;
  }
  return (n) => {
    const hay = `${n.title}\n${n.path ?? ''}`.toLowerCase();
    if (!words.every((w) => hay.includes(w))) {
      return false;
    }
    if (excluded.some((w) => hay.includes(w))) {
      return false;
    }
    return true;
  };
}

/** Colours that read on white and on near-black alike (Tailwind 500s). */
export const PALETTE = [
  '#6366f1',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#8b5cf6',
  '#f97316',
  '#14b8a6',
];
export const NEUTRAL = '#94a3b8';

export const KIND_COLORS: Record<GraphNodeKind, string> = {
  doc: '#6366f1',
};

export interface ViewNode {
  id: string;
  title: string;
  kind: GraphNodeKind;
  path?: string;
  color: string;
  radius: number;
  degree: number;
  isCenter: boolean;
}

export interface ViewLink {
  source: string;
  target: string;
  count: number;
  tree?: boolean;
}

export interface LegendEntry {
  label: string;
  color: string;
  count: number;
  query?: string;
}

export interface GraphView {
  nodes: ViewNode[];
  links: ViewLink[];
  legend: LegendEntry[];
}

function topFolder(n: GraphNode): string {
  const p = n.path ?? '';
  const i = p.indexOf('/');
  return i === -1 ? '' : p.slice(0, i);
}

/** The `max` most frequent values, most frequent first (ties by name). */
function topValues(values: string[], max: number): string[] {
  const counts = new Map<string, number>();
  for (const v of values) {
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, max)
    .map(([v]) => v);
}

export function nodeRadius(
  degree: number,
  settings: Pick<GraphSettings, 'sizeByDegree' | 'nodeSize'>,
  _kind: GraphNodeKind,
): number {
  const base = settings.sizeByDegree
    ? 3 + Math.min(16, Math.sqrt(degree) * 1.5)
    : 4.5;
  return base * settings.nodeSize;
}

export interface LegendLabels {
  rootFolder: string;
}

const DEFAULT_LABELS: LegendLabels = { rootFolder: '/' };

export function nodeLabel(n: Pick<GraphNode, 'title'>): string {
  return n.title;
}

/**
 * GraphResponse + settings → the nodes and links to draw, with colours and sizes.
 * `centerId` (the local graph) is always kept, whatever the filters say.
 */
export function buildGraphView(
  data: Pick<GraphResponse, 'nodes' | 'edges'>,
  settings: GraphSettings,
  centerId?: string,
  labels: LegendLabels = DEFAULT_LABELS,
): GraphView {
  const match = compileQuery(settings.search);

  const keptPages = new Set<string>();
  for (const n of data.nodes) {
    if (isPageNode(n) && (n.id === centerId || !match || match(n))) {
      keptPages.add(n.id);
    }
  }
  const kept = (id: string) => keptPages.has(id);
  let edges: GraphEdge[] = data.edges.filter(
    (e) => kept(e.source) && kept(e.target),
  );

  const degree = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    let s = degree.get(a);
    if (!s) {
      degree.set(a, (s = new Set()));
    }
    s.add(b);
  };
  for (const e of edges) {
    add(e.source, e.target);
    add(e.target, e.source);
  }
  let nodes = data.nodes.filter((n) => kept(n.id));
  if (!settings.showOrphans) {
    nodes = nodes.filter(
      (n) => n.id === centerId || (degree.get(n.id)?.size ?? 0) > 0,
    );
    const ids = new Set(nodes.map((n) => n.id));
    edges = edges.filter((e) => ids.has(e.source) && ids.has(e.target));
  }

  const groups = settings.groups
    .map((g) => ({ ...g, match: compileQuery(g.query) }))
    .filter((g) => g.match !== null);
  const legend: LegendEntry[] = [];
  const presetColor = new Map<string, string>();
  let presetOf: (n: GraphNode) => string | undefined = () => undefined;
  const pages = nodes.filter((n) => isPageNode(n));
  if (settings.colorBy === 'folder') {
    presetOf = (n) => (isPageNode(n) ? topFolder(n) : undefined);
    topValues(pages.map(topFolder), PALETTE.length).forEach((f, i) =>
      presetColor.set(f, PALETTE[i]!),
    );
  }
  const counts = new Map<string, number>();
  const groupCounts = groups.map(() => 0);
  const colorOf = (n: GraphNode): string => {
    for (let i = 0; i < groups.length; i++) {
      if (groups[i]!.match!(n)) {
        groupCounts[i]!++;
        return groups[i]!.color;
      }
    }
    const key = presetOf(n);
    if (key !== undefined && presetColor.has(key)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
      return presetColor.get(key)!;
    }
    return KIND_COLORS[n.kind] ?? NEUTRAL;
  };

  const viewNodes: ViewNode[] = nodes.map((n) => {
    const d = degree.get(n.id)?.size ?? 0;
    return {
      id: n.id,
      title: nodeLabel(n),
      kind: n.kind,
      path: n.path,
      color: colorOf(n),
      radius: nodeRadius(d, settings, n.kind),
      degree: d,
      isCenter: n.id === centerId,
    };
  });

  groups.forEach((g, i) =>
    legend.push({
      label: g.query,
      color: g.color,
      count: groupCounts[i]!,
      query: g.query,
    }),
  );
  for (const [key, color] of presetColor) {
    const count = counts.get(key) ?? 0;
    if (count === 0) {
      continue;
    }
    legend.push({
      label: key === '' ? labels.rootFolder : `${key}/`,
      color,
      count,
      query: key === '' ? undefined : key,
    });
  }

  return {
    nodes: viewNodes,
    links: edges.map((e) => ({
      source: e.source,
      target: e.target,
      count: e.count,
    })),
    legend,
  };
}

/** Ids of a node and its direct neighbours among `links` — what hover highlights. */
export function neighbourhood(id: string, links: ViewLink[]): Set<string> {
  const out = new Set([id]);
  for (const l of links) {
    if (l.source === id) {
      out.add(l.target);
    } else if (l.target === id) {
      out.add(l.source);
    }
  }
  return out;
}
